import { ChatType } from '@/types/ai'
import { ResponseWithPagination } from '@/types/api'
import { User } from '@/types/user'
import { TemplateType } from '../template/types'

const FILE_PERMISSIONS = ['owner', 'editor', 'viewer'] as const

export type FilePermission = (typeof FILE_PERMISSIONS)[number]

export enum FilePermissionEnum {
    OWNER = 'owner',
    EDITOR = 'editor',
    VIEWER = 'viewer',
}

export type FileExplorerSelectedFileOptions = {
    closeOnSelect?: boolean
    multiple?: boolean
    selectedFiles?: DocumentInterface[]
    primaryButtonProps: {
        display: boolean
        primaryText: string
        /**
         * This is the text to display when a file is selected, if it is not provided, the primaryText will be used
         */
        secondaryText: string
        onClick: (arg: { file: FileType | DocumentInterface }) => void
        isFileMutating?: boolean
    }
    secondaryButtonProps?: {
        display: boolean
        primaryText: string
        /**
         * This is the text to display when a file is selected, if it is not provided, the primaryText will be used
         */
        secondaryText: string
        onClick: (arg: { file: FileType | DocumentInterface }) => void
        isFileMutating?: boolean
    }
}

export interface DocumentInterface {
    owner: {
        id: string
        name: string
        user: User
        email?: string
    }
    data: any
    _id: string
    name: string
    id: string
    createdAt: string
    last_opened: string
    description: string
    updatedAt: string
    data_type: 'info' | 'image' | 'video' | 'pdf' | null
    isFolder: boolean
    parent: DocumentInterface
    url: string
    content: string
    json: Record<string, unknown>
    html: string
    size: number
    type: string
    tag: string
    status: string
    favorite: boolean
    breadcrumbs: string[]
    id_element?: string
    computed_breadcrumbs: {
        name: string
        id: string
        is_folder?: boolean
    }[]
    related_files: FileType[]
    conversations: ChatType[]
    soft_deleted_at: string
    auto_delete_at?: string
    prompt_template: {
        json: Object
        html: string
        text: string
    }
    output_template: {
        json: Object
        html: string
        text: string
    }
    template?: TemplateType
    permissions: {
        role: string
        account: string
    }[]
    file_permission: FilePermissionEnum
    /**
     * @deprecated The `/document?id=` endpoint no longer embeds children to
     * avoid 16MB BSON overflows on large folders. Use `useInfiniteDocuments`
     * or `useQueryDocuments` with `parent: <folderId>` to list children.
     * This field remains optional only so legacy/archived components compile;
     * new code must not read it.
     */
    items?: DocumentInterface[]
    interviewer: boolean
    org: string
    has_inherited_permissions: boolean
    content_last_updated: string
    shared_with_workspace?: { enabled: boolean; role: string }
    path?: string
    metadata?: {
        system_folder?: boolean
        execution_start_date?: string
        commzone_code?: string
        system_folder_type?: string
        brand_files_category?: boolean
        wizard_name?: string
        wizard_id?: string
        wizard_execution_id?: string
        [key: string]: any
    }
    has_access?: boolean
    pending_document_access_request?: {
        id: string
        status: 'pending' | 'approved' | 'denied'
        requested_at: string
    }
}

export type FileType = DocumentInterface

export type DocumentVersionReason = 'autosave_bucketed' | 'manual' | 'restore'

export interface DocumentVersionSummary {
    _id: string
    documentId: string
    createdAt: string
    updatedAt: string
    createdBy?: { _id: string; email?: string; user?: { fullname?: string; email?: string } } | null
    reason: DocumentVersionReason
    note?: string | null
    contentHash?: string | null
}

export interface DocumentVersion extends DocumentVersionSummary {
    content?: string | null
    html?: string | null
    json?: Record<string, unknown> | null
    prompt_template?: {
        json: Record<string, unknown>
        html: string
        text: string
    } | null
    output_template?: {
        json: Record<string, unknown>
        html: string
        text: string
    } | null
}

export type ExportFormat = 'pdf' | 'txt' | 'html' | 'markdown'

export interface ExportStatsResponse {
    fileCount: number
    folderCount: number
    totalSize: number
    totalSizeMB: number
    hierarchy: ExportHierarchyItem[]
    exceedsLimit: boolean
    limitMB: number
}

export interface ExportHierarchyItem {
    id: string
    name: string
    type: 'file' | 'folder'
    size?: number
    children?: ExportHierarchyItem[]
}

export interface GlobalSearchResult {
    id: string
    name: string
    type: string
    isFolder: boolean
    snippet: string
    source: 'owned' | 'shared'
    thumbnailUrl?: string
    ownerName: string
    dataType: string
    updatedAt: string
}

export interface GlobalSearchResponse {
    results: GlobalSearchResult[]
    total: number
    page: number
    limit: number
    hasMore: boolean
}

export type Item = DocumentInterface

export type APIDocumentType = ResponseWithPagination<DocumentInterface>
export type APIDocumentInterface = ResponseWithPagination<DocumentInterface>
export type APIFileType = ResponseWithPagination<FileType>
export type APIItem = ResponseWithPagination<Item>
export type APIRecentFiles = {
    nodes: DocumentInterface[]
    files: DocumentInterface[]
}
